import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Briefcase, Clock, CheckCircle, Users, Search, Plus, Filter } from 'lucide-react';
import { useEffect } from 'react';
import { useEntity } from '../../hooks/useEntity';
import { getProjects } from '../../lib/supabase/projects';
import ProjectForm from './ProjectForm';
import ProjectDetails from './ProjectDetails';

import type { Project } from '../../types/project';

export default function ProjectList() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const { entity } = useEntity();
  const location = useLocation();
  
  useEffect(() => {
    // Check if we were navigated here with a projectId
    if (location.state?.projectId) {
      setSelectedProject(location.state.projectId);
      // Clear the state to prevent it from persisting on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);
  
  useEffect(() => {
    loadProjects();
  }, [entity?.id]);
  
  const loadProjects = async () => {
    if (!entity?.id) {
      setError('No entity selected');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const data = await getProjects(entity.id);
      setProjects(data);
    } catch (err) {
      console.error('Failed to load projects:', err);
      setError('Failed to load projects. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateProject = () => {
    setShowProjectForm(true);
  };
  
  const handleProjectCreated = () => {
    setShowProjectForm(false);
    loadProjects();
  };
  
  const handleProjectClick = (projectId: string) => {
    setSelectedProject(projectId);
  };
  
  const handleBackToProjects = () => {
    setSelectedProject(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'planning':
        return 'bg-yellow-100 text-yellow-700';
      case 'in_progress':
        return 'bg-blue-100 text-blue-700';
      case 'review':
        return 'bg-purple-100 text-purple-700';
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'on_hold':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress < 30) return 'bg-yellow-500';
    if (progress < 70) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // If a project is selected, show its details
  if (selectedProject) {
    return (
      <ProjectDetails 
        projectId={selectedProject} 
        onBack={handleBackToProjects}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex space-x-2">
          <button 
            onClick={handleCreateProject}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>New Project</span>
          </button>
          <div className="relative">
            <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2">
              <Filter className="w-4 h-4" />
              <span>Filter</span>
            </button>
          </div>
        </div>
        <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-4 w-full md:w-auto">
          <div className="relative">
            <input
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full md:w-64"
            />
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
          </div>
          <select 
            className="border border-gray-300 rounded-lg px-3 py-2 text-gray-700"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="planning">Planning</option>
            <option value="in_progress">In Progress</option>
            <option value="review">Review</option>
            <option value="completed">Completed</option>
            <option value="on_hold">On Hold</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 bg-white rounded-xl shadow">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading projects...</p>
        </div>
      ) : error ? (
        <div className="text-center py-12 bg-white rounded-xl shadow">
          <p className="text-red-500 mb-4">{error}</p>
          <button 
            onClick={loadProjects}
            className="text-blue-500 hover:text-blue-600"
          >
            Try Again
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {filteredProjects.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl shadow">
              <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No projects found</h3>
              <p className="text-gray-500">Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            filteredProjects.map(project => (
              <div key={project.id} className="bg-white rounded-xl shadow p-6 hover:shadow-md transition-shadow">
                <button 
                  onClick={() => handleProjectClick(project.id)}
                  className="w-full text-left"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div className="mb-4 md:mb-0">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-xl font-semibold text-gray-900">{project.name}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(project.status)}`}>
                          {project.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </div>
                      <p className="text-gray-600 mt-1">{project.description}</p>
                      <div className="flex items-center space-x-4 mt-2">
                        {project.due_date && (
                          <span className="text-gray-500 text-sm flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            Due {new Date(project.due_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <div className="text-sm text-gray-500">
                        <div className="flex items-center">
                          <CheckCircle className="w-4 h-4 mr-1 text-green-500" />
                          <span>{project.tasks?.completed || 0}/{project.tasks?.total || 0} tasks</span>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        <div className="flex items-center">
                          <Users className="w-4 h-4 mr-1 text-blue-500" />
                          <span>{project.team_size || 0} members</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-500">Progress</span>
                      <span className="text-sm font-medium text-gray-700">{project.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${getProgressColor(project.progress)}`}
                        style={{ width: `${project.progress}%` }}
                      />
                    </div>
                  </div>
                </button>
              </div>
            ))
          )}
        </div>
      )}
      
      {showProjectForm && (
        <ProjectForm 
          onClose={() => setShowProjectForm(false)} 
          onSuccess={handleProjectCreated}
        />
      )}
    </div>
  );
}